
import throttle from 'lodash/throttle'

const LIKED_CONTENT_STORAGE_OLD = 'liked-articles'
const LIKED_CONTENT_STORAGE = 'liked-content'

export default {
  name: 'EoLikeButton',
  props: {
    /**
     * Content ID of the content item
     */
    contentId: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * Amount of likes already given for this item
     */
    likes: {
      type: Number,
      default: 0,
    },
    /**
     * Prepr model name of the content item
     */
    contentType: {
      type: String,
      default: null,
    },
    /**
     * Label to encourage the act of liking
     */
    labelAction: {
      type: String,
      default: 'Geef een hartje',
    },
    /**
     * Label of one like in the past
     */
    labelSingularPast: {
      type: String,
      default: 'gaf een hartje',
    },
    /**
     * Label of multiple likes in the past
     */
    labelPluralPast: {
      type: String,
      default: 'gaven een hartje',
    },
    /**
     * Hide text label and only show amount
     */
    onlyAmount: {
      type: Boolean,
      default: false,
    },
    /**
     * Invert colors of the button
     */
    darkMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Float the button at the bottom of the screen
     */
    floating: {
      type: Boolean,
      default: false,
    },
    /**
     * Button size
     */
    size: {
      type: String,
      default: 'lg',
    },
  },
  data () {
    return {
      // Data
      likedContent: [],
      // UI
      isFloatingButtonHidden: false,
      isLiked: false,
      previousScrollY: 0,
    }
  },
  computed: {
    totalLikes () {
      return this.likes + this.isLiked
    },
    label () {
      if (this.isLiked && this.totalLikes === 1) {
        return `Jij ${this.labelSingularPast}`
      }
      if (this.isLiked && this.totalLikes === 2) {
        return `Jij en ${this.likes} ander ${this.labelPluralPast}`
      }
      if (this.isLiked && this.totalLikes > 2) {
        return `Jij en ${this.likes} anderen ${this.labelPluralPast}`
      }
      if (!this.floating && !this.isLiked && this.likes === 1) {
        return `${this.likes} ander ${this.labelSingularPast}`
      }
      if (!this.floating && !this.isLiked && this.likes > 1) {
        return `${this.likes} anderen ${this.labelPluralPast}`
      }
      return this.labelAction
    },
  },
  updated () {
    this.onPageScroll()
    this.getStoredLikes()
  },
  mounted () {
    this.previousScrollY = window.scrollY

    this.getStoredLikes()
    window.addEventListener('likes-changed', this.getStoredLikes)
    window.addEventListener('scroll', throttle(this.onPageScroll, 300))
    
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onPageScroll)
    window.removeEventListener('likes-changed', this.getStoredLikes)
  },
  methods: {
    onClick () {
      if (this.isLiked) {
        this.removeLike()
      } else {
        this.addLike()
      }
    },
    addLike () {
      this.isLiked = true
      this.likedContent.push(this.contentId)
      this.setStoredLikes()

      // Emit event
      this.$emit('like')
      
      // Track event
      this.trackEvent('like')
    },
    removeLike () {
      this.isLiked = false
      this.likedContent = this.likedContent.filter(id => id != this.contentId)
      this.setStoredLikes()

      // Emit event
      this.$emit('unlike')

      // Track event
      this.trackEvent('unlike')
    },
    getStoredLikes () {
      try{
        const storedLikes = [...(JSON.parse(localStorage.getItem(LIKED_CONTENT_STORAGE_OLD)) || []), ...(JSON.parse(localStorage.getItem(LIKED_CONTENT_STORAGE)) || [])]
        this.likedContent = storedLikes
        this.isLiked = storedLikes.includes(this.contentId)
      } catch (e) {
        this.$log.error(e)
        return false
      }
    },
    setStoredLikes () {
      try {
        localStorage.setItem(LIKED_CONTENT_STORAGE, JSON.stringify(this.likedContent))
        localStorage.removeItem(LIKED_CONTENT_STORAGE_OLD) // remove deprecated storage
      } catch (e) {
        this.$log.error(e)
      }
      window.dispatchEvent(new CustomEvent('likes-changed'))
    },
    trackEvent (event) {
      try {
        if (window?.prepr && this.contentId) {
          if(event === 'like') {
            window.prepr('event', 'Like', { id: this.contentId, constraint: true })
            } else {
            window.prepr('event', 'Unlike', { id: this.contentId, constraint: true })
          }
        }
        if (this.$gtm && event === 'like') {
          this.$gtm.push({
            event: 'eo_like',
            item_id: this.contentId,
            content_type: this.contentType,
          })
        }
      } catch (error) {
        const referrer = process.client && document?.referrer
        const {
          context,
          message = 'Er is iets mis gegaan met het versturen van trackEvent Like',
          path = referrer,
          severity,
          statusCode = 500,
        } = this.error

        this.$bugsnag.notify(new Error(`${statusCode}: ${message}`), (e) => {
          e.severity = severity || (statusCode === 404 ? 'info' : 'error')
          e.context = context || path
          e.addMetadata('Error info', {
            ...this.error,
            referrer,
          })
        })
      }
    },
    async onPageScroll () {
      if (!this.floating) return
      if (process.server || !window) return
      const scrollDistance = window.scrollY - this.previousScrollY
      const isScrollingUp = window.scrollY - this.previousScrollY <= 0
      const isVisible = isScrollingUp || window.scrollY < 300

      // Big scroll motion
      if (scrollDistance < -15 || scrollDistance > 30) {
        this.toggleFloatingButton(isVisible)
      }

      this.previousScrollY = window.scrollY
    },
    toggleFloatingButton: throttle(function (isVisible) {
      this.isFloatingButtonHidden = !isVisible
    }, 300),
  },
}
